import { useRef, useState } from 'react';
import { cx } from '@parrot/utils';
import type { DisplayProduct, ProductClickHandler } from '../../../../types';
import styles from './ProductCard.module.css';

/**
 * Presentational card. By default the click is a plain link to the product's URL —
 * the destination app enforces access on arrival, so there's no auth here. A host
 * that needs more (SSO, in-app routing, blocking unlicensed) passes `onProductClick`
 * and calls `event.preventDefault()` to take over. Licensing badges are informational.
 */
export function ProductCard({
  product,
  onProductClick,
}: {
  product: DisplayProduct;
  onProductClick?: ProductClickHandler;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const handleMouseEnter = () => {
    if (!product.video || !videoRef.current) return;
    videoRef.current
      .play()
      .then(() => setPlaying(true))
      .catch(() => {
        // Autoplay blocked — stay on the poster image.
      });
  };

  const handleMouseLeave = () => {
    if (!product.video || !videoRef.current) return;
    setPlaying(false);
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  };

  return (
    <a
      href={product.href || '#'}
      target={'_blank'}
      rel={'noopener noreferrer'}
      className={styles.card}
      onClick={(event) => onProductClick?.(product, event)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.media}>
        {product.image ? (
          <img
            src={product.image}
            alt={product.displayName}
            loading={'lazy'}
            style={{ opacity: playing ? 0 : 1 }}
          />
        ) : (
          <span className={styles.placeholder}>Image coming soon</span>
        )}
        {product.video && (
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            preload={'auto'}
            src={product.video}
            style={{ opacity: playing ? 1 : 0 }}
          />
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.name}>{product.displayName}</div>
        <p className={styles.desc}>{product.description}</p>
        {product.entityScope.length > 0 && (
          <div className={styles.entities}>
            {product.entityScope.map((entity) => (
              <span key={entity} className={styles.entity}>
                {entity}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className={styles.footer}>
        {product.isAddon && <span className={cx(styles.badge, styles.badgeAddon)}>Add-on</span>}
        <span
          className={cx(styles.badge, product.licensed ? styles.badgeLicensed : styles.badgeLocked)}
        >
          {product.licensed ? 'Licensed' : 'Locked'}
        </span>
        <span className={styles.arrow} aria-hidden={'true'}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M7 17L17 7" />
            <path d="M7 7h10v10" />
          </svg>
        </span>
      </div>
    </a>
  );
}
